import { useEffect, useRef, useState, useCallback } from 'react';

const defaultReload = () => window.location.reload();

export interface UseBundleUpdateOptions {
  enabled?: boolean;
  onReload?: () => void;
}

export interface UseBundleUpdateReturn {
  updateAvailable: boolean;
  applyUpdate: () => void;
}

/**
 * Spawns a web worker that checks /version.json once per day after 8 PM.
 * When a new build hash is detected, `updateAvailable` flips to true.
 * Call `applyUpdate()` to reload the page.
 */
export function useBundleUpdate({
  enabled = true,
  onReload = defaultReload,
}: UseBundleUpdateOptions = {}): UseBundleUpdateReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (!enabled || typeof Worker === 'undefined') return undefined;

    const worker = new Worker('/bundle-update-worker.js');
    workerRef.current = worker;

    worker.addEventListener('message', event => {
      if (event.data?.type === 'UPDATE_AVAILABLE') {
        setUpdateAvailable(true);
      }
    });

    worker.postMessage({ type: 'START' });

    return () => {
      worker.postMessage({ type: 'STOP' });
      worker.terminate();
      workerRef.current = null;
    };
  }, [enabled]);

  const applyUpdate = useCallback(() => {
    onReload();
  }, [onReload]);

  return { updateAvailable, applyUpdate };
}
